import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Shop } from './components/Shop';
import { Admin } from './components/Admin';
import { Footer } from './components/Footer';
import { ProductModal } from './components/ProductModal';
import { CartDrawer } from './components/CartDrawer';
import { Product, ViewState, SiteConfig } from './types';
import { Loader2, CheckCircle2, Download, Copy, Clock, AlertOctagon, X, Lock } from 'lucide-react';
import { DB, DownloadLink } from './services/db';
import { checkInvoiceStatus } from './services/payment';
const BackgroundCanvas = lazy(() =>
  import('./components/ui/BackgroundCanvas').then((m) => ({ default: m.BackgroundCanvas }))
);
import { MobileBackdrop } from './components/ui/MobileBackdrop';
import { Lightbox } from './components/ui/Lightbox';
import { useLanguage } from './services/languageContext';
import { useEnableWebGLBackground } from './hooks/useMediaQuery';
import { preloadImages } from './services/mediaCache';
import { isVideoUrl } from './services/videoUtils';

const EXPIRATION_TIME_MS = 60 * 60 * 1000; // 1 Hour

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewState>('landing');
  const [products, setProducts] = useState<Product[]>([]);
  const [siteConfig, setSiteConfig] = useState<SiteConfig>({ heroVideos: [], featuredProductIds: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [cart, setCart] = useState<Product[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  
  // Success Modal States
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string>('');
  
  const { lang, t } = useLanguage();
  const enableWebGL = useEnableWebGLBackground();

  // State for Fullscreen Media (Video OR Image from Hero)
  const [mediaViewer, setMediaViewer] = useState<{ url: string; type: 'video' | 'image' } | null>(null);

  // Secure download links (fetched via RPC after payment)
  const [purchasedDownloads, setPurchasedDownloads] = useState<DownloadLink[]>([]);

  const refreshData = async () => {
    try {
      // Parallel fetch for speed
      const [loadedProducts, loadedConfig] = await Promise.all([
        DB.getProducts(),
        DB.getSiteConfig()
      ]);
      setProducts(loadedProducts || []); 
      setSiteConfig(loadedConfig);
    } catch (error) {
      console.error("Critical: Failed to load data.", error);
    }
  };

  useEffect(() => {
    const init = async () => {
        setIsLoading(true);
        await refreshData();
        setIsLoading(false);
    };
    init();

    // --- PAYMENT VERIFICATION FLOW ---
    const checkPayment = async () => {
      try {
          const urlParams = new URLSearchParams(window.location.search);
          const paymentParam = urlParams.get('payment');
          
          if (paymentParam === 'verifying' || paymentParam === 'success') {
            setIsVerifying(true);
            const pendingInvoiceId = localStorage.getItem('ofm_pending_invoice');
            
            if (pendingInvoiceId) {
                const invId = parseInt(pendingInvoiceId, 10);
                const status = await checkInvoiceStatus(invId);
                
                if (status === 'paid') {
                    const dbSuccess = await DB.updateOrderStatus(invId, 'paid');
                    if (!dbSuccess) console.error("DB Update Failed - Check RLS Policies");
                    
                    localStorage.removeItem('ofm_pending_invoice');
                    await handlePaymentSuccess(true, invId);
                } else if (status === 'active') {
                    showNotification("Payment not yet confirmed. Please wait.");
                } else if (status === 'expired') {
                    showNotification("Invoice expired.");
                } else {
                    showNotification("Payment check failed.");
                }
            } else {
                // Legacy / Refresh check
                const ts = localStorage.getItem('ofm_payment_ts');
                if (ts && Date.now() - parseInt(ts) < EXPIRATION_TIME_MS) {
                    handlePaymentSuccess(false); // Restore Session
                }
            }
            
            setIsVerifying(false);
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }
      } catch (e) {
          console.warn("Payment verification error", e);
          setIsVerifying(false);
      }
    };
    
    checkPayment();
  }, []);

  // Expiration Logic Hook
  useEffect(() => {
    if (showSuccessModal && !isLinkExpired) {
       const timer = setInterval(() => {
          const storedTime = localStorage.getItem('ofm_payment_ts');
          if (storedTime) {
             const elapsed = Date.now() - parseInt(storedTime, 10);
             const remaining = EXPIRATION_TIME_MS - elapsed;
             
             if (remaining <= 0) {
                setIsLinkExpired(true);
                clearInterval(timer);
                setTimeLeft("00:00");
             } else {
                const minutes = Math.floor(remaining / 60000);
                const seconds = ((remaining % 60000) / 1000).toFixed(0);
                setTimeLeft(`${minutes}:${parseInt(seconds) < 10 ? '0' : ''}${seconds}`);
             }
          }
       }, 1000);
       return () => clearInterval(timer);
    }
  }, [showSuccessModal, isLinkExpired]);

  useEffect(() => {
    if (!isLoading && products.length > 0) {
      const imageUrls = products
        .slice(0, 12)
        .map(p => p.coverImage)
        .filter(u => u && !isVideoUrl(u));
      preloadImages(imageUrls);

      const heroImageUrls = siteConfig.heroVideos
        .filter(h => h.type === 'image' && h.url)
        .map(h => h.url as string);
      if (heroImageUrls.length) preloadImages(heroImageUrls);
    }
  }, [isLoading, products, siteConfig]);

  const handlePaymentSuccess = async (forceNewSession: boolean = false, paidInvoiceId?: number) => {
    const ts = Date.now();

    if (forceNewSession) {
        localStorage.setItem('ofm_payment_ts', ts.toString());
        setIsLinkExpired(false);
    } else {
        const existingTs = localStorage.getItem('ofm_payment_ts');
        if (existingTs) {
            const elapsed = Date.now() - parseInt(existingTs, 10);
            if (elapsed > EXPIRATION_TIME_MS) {
                setIsLinkExpired(true);
            } else {
                setIsLinkExpired(false);
            }
        } else {
            localStorage.setItem('ofm_payment_ts', ts.toString());
            setIsLinkExpired(false);
        }
    }

    if (paidInvoiceId) {
      localStorage.setItem('ofm_paid_invoice', paidInvoiceId.toString());
    }

    const invoiceId = paidInvoiceId || parseInt(localStorage.getItem('ofm_paid_invoice') || '0', 10);
    if (invoiceId) {
      const links = await DB.getDownloadLinks(invoiceId);
      setPurchasedDownloads(links);
    }

    setShowSuccessModal(true);
    setCart([]); 
  };

  const handleAddToCart = (product: Product) => {
    setCart([...cart, product]);
    showNotification(`Asset Acquired: ${product.title}`);
  };

  const handleRemoveFromCart = (id: string) => {
    const index = cart.findIndex(item => item.id === id);
    if (index > -1) {
      const newCart = [...cart];
      newCart.splice(index, 1);
      setCart(newCart);
    }
  };

  const showNotification = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  // Admin handlers...
  const handleAddProduct = (newProduct: Product) => { setProducts(prev => [newProduct, ...prev]); showNotification("Database Updated"); };
  const handleUpdateProduct = (updatedProduct: Product) => { setProducts(prev => prev.map(p => p.id === updatedProduct.id ? updatedProduct : p)); showNotification("Asset Synced"); };
  const handleDeleteProduct = (id: string) => { setProducts(prev => prev.filter(p => p.id !== id)); showNotification("Asset Purged"); };

  const handleConfigUpdate = async () => {
    await refreshData();
    showNotification("Global Config Updated");
  };

  if (currentView === 'admin') {
    return (
      <div className="min-h-screen bg-[#030304] text-white">
        <div className="bg-noise-overlay z-50 opacity-[0.03] pointer-events-none" />
        <Admin 
          products={products} 
          siteConfig={siteConfig}
          onAddProduct={handleAddProduct}
          onUpdateProduct={handleUpdateProduct}
          onDeleteProduct={handleDeleteProduct}
          onConfigUpdate={handleConfigUpdate}
          onExit={() => setCurrentView('landing')}
        />
      </div>
    );
  }

  return (
    <div key={lang} className="min-h-screen bg-background text-white relative font-sans selection:bg-white selection:text-black overflow-x-hidden">
      
      {/* WebGL only on tablet+ — faster LCP and less battery on phones */}
      {enableWebGL ? (
        <Suspense fallback={<MobileBackdrop />}>
          <BackgroundCanvas />
        </Suspense>
      ) : (
        <MobileBackdrop />
      )}

      {/* TEXTURE LAYERS */}
      <div className="bg-noise-overlay z-0" />
      
      {/* Subtle top spotlight to help readability if WebGL is too dark */}
      <div className="fixed top-[-20%] left-0 right-0 h-[600px] bg-gradient-to-b from-white/[0.04] to-transparent pointer-events-none z-[2]" />

      <Navbar 
        currentView={currentView} 
        onChangeView={setCurrentView} 
        cartCount={cart.length}
        onCartClick={() => setIsCartOpen(true)}
      />

      <main className="relative z-10 pt-20 md:pt-24 min-h-screen">
        {currentView === 'landing' && (
          <Hero 
             onShopNow={() => setCurrentView('shop')} 
             products={products}
             siteConfig={siteConfig}
             onProductClick={setSelectedProduct}
             onOpenMedia={(url, type) => setMediaViewer({ url, type })}
          />
        )}

        {currentView === 'shop' && (
          isLoading ? (
            <div className="flex h-[60vh] items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-white/10 blur-xl rounded-full"></div>
                <Loader2 className="animate-spin text-white relative z-10" size={32} />
              </div>
            </div>
          ) : (
            <div className="max-w-[1600px] mx-auto px-3 sm:px-6 pb-16 sm:pb-20">
              <Shop 
                products={products} 
                onProductClick={setSelectedProduct} 
                onAddToCart={handleAddToCart}
              />
            </div>
          )
        )}
      </main>

      <Footer onChangeView={setCurrentView} />

      {/* DRAWERS & MODALS */}
      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
        cartItems={cart} 
        onRemoveItem={handleRemoveFromCart}
      />

      {selectedProduct && (
        <ProductModal 
          product={selectedProduct} 
          onClose={() => setSelectedProduct(null)} 
          onAddToCart={() => {
            handleAddToCart(selectedProduct);
            setSelectedProduct(null);
          }}
        />
      )}

      {/* FULL SCREEN MEDIA PLAYER */}
      {mediaViewer && (
        <Lightbox 
          src={mediaViewer.url} 
          type={mediaViewer.type} 
          onClose={() => setMediaViewer(null)} 
        />
      )}

      {/* VERIFICATION LOADING OVERLAY */}
      {isVerifying && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/90 backdrop-blur-xl">
            <div className="flex flex-col items-center gap-4">
                <Loader2 className="animate-spin text-indigo-500" size={48} />
                <p className="text-white font-mono uppercase tracking-widest text-sm">Verifying Blockchain Transaction...</p>
            </div>
        </div>
      )}

      {/* SUCCESS VAULT MODAL */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl animate-fade-in">
           <div className="relative w-full max-w-lg bg-[#050505] border border-white/10 rounded-3xl overflow-hidden shadow-2xl flex flex-col">
              
              {/* Top Gradient */}
              <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-${isLinkExpired ? 'red' : 'green'}-500 to-transparent opacity-80`} />

              {/* Header */}
              <div className="p-8 pb-0 text-center relative z-10">
                 <div className={`w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center border ${isLinkExpired ? 'bg-red-900/10 border-red-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                    {isLinkExpired ? <AlertOctagon className="text-red-500" size={32} /> : <CheckCircle2 className="text-green-500" size={32} />}
                 </div>
                 
                 <h2 className="text-2xl font-display font-black text-white mb-2 uppercase tracking-tight">
                    {isLinkExpired ? t('success_expired_title') : t('success_title')}
                 </h2>
                 <p className="text-sm text-gray-400 font-light leading-relaxed px-4">
                    {isLinkExpired ? t('success_expired_desc') : t('success_subtitle')}
                 </p>
              </div>

              {/* Content */}
              <div className="p-8">
                 {!isLinkExpired ? (
                    <div className="space-y-6">
                        {/* Countdown */}
                        <div className="flex justify-between items-center bg-[#111] p-3 rounded-xl border border-white/5">
                           <span className="text-[10px] uppercase font-bold text-gray-500 tracking-widest flex items-center gap-2">
                             <Clock size={12} /> {t('success_expires')}
                           </span>
                           <span className="font-mono text-white text-sm font-bold animate-pulse text-red-400">
                             {timeLeft}
                           </span>
                        </div>

                        {/* Secure Download Links (fetched via RPC) */}
                        <div className="space-y-3 max-h-60 overflow-y-auto custom-scrollbar">
                           {purchasedDownloads.length > 0 ? purchasedDownloads.map((item) => (
                             <div key={item.id} className="bg-white/[0.03] border border-white/5 p-4 rounded-xl group hover:border-white/20 transition-colors">
                                <div className="flex justify-between items-start mb-3">
                                   <h4 className="text-sm font-bold text-white">{item.title}</h4>
                                </div>
                                
                                <div className="flex flex-col gap-2">
                                  <a 
                                    href={item.digitalAssetUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 w-full bg-white text-black py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-gray-200 transition-colors"
                                  >
                                     <Download size={14} /> {t('success_download')}
                                  </a>

                                  {item.digitalAssetInstructions && (
                                    <div className="mt-1 bg-black/40 p-2 rounded border border-white/5 flex justify-between items-center">
                                       <div className="flex items-center gap-2">
                                          <div className="w-1 h-1 bg-gray-500 rounded-full" />
                                          <span className="text-[10px] text-gray-400 font-mono">{item.digitalAssetInstructions}</span>
                                       </div>
                                       <button 
                                         onClick={() => {navigator.clipboard.writeText(item.digitalAssetInstructions || ''); showNotification('Copied');}}
                                         className="text-gray-500 hover:text-white"
                                       >
                                          <Copy size={12} />
                                       </button>
                                    </div>
                                  )}
                                </div>
                             </div>
                           )) : (
                             <div className="text-center py-6 text-gray-500 text-xs">
                                <p className="uppercase tracking-widest font-bold mb-2">Processing...</p>
                                <p className="text-[10px] text-gray-600">Contact @miranovui on Telegram if links don't appear</p>
                             </div>
                           )}
                        </div>
                    </div>
                 ) : (
                    // Expired State
                    <div className="text-center py-6">
                        <div className="inline-block p-4 border border-dashed border-white/10 rounded-full mb-4">
                           <Lock size={24} className="text-gray-600" />
                        </div>
                        <p className="text-xs text-gray-500 font-mono uppercase tracking-widest">Access Revoked</p>
                    </div>
                 )}
              </div>

              {/* Footer Actions */}
              <div className="p-6 border-t border-white/5 bg-[#080808]">
                 <button 
                   onClick={() => setShowSuccessModal(false)}
                   className="w-full py-3 text-xs font-bold text-gray-500 hover:text-white uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                 >
                    <X size={14} /> {t('success_close')}
                 </button>
              </div>

           </div>
        </div>
      )}

      {/* NOTIFICATION - PREMIUM TOAST */}
      {notification && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-[#0F0F12] border border-white/10 text-white px-6 py-3 rounded-full shadow-2xl z-[200] animate-slide-up flex items-center gap-3 backdrop-blur-md">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
          <span className="font-medium text-sm tracking-wide">{notification}</span>
        </div>
      )}
    </div>
  );
};

export default App;
